import pako from "pako";
import {ROUTE_POINT_TYPES, THIN_SPACE_CHAR,} from "../constants";
import logger from "../logger";
import PointUrlParameterParser from "./PointUrlParameter";
import ParsedPoint from "./ParsedPoint";

const hasType = (type, isLegacyLinkMode, expectedType, expectedLegacyType) =>
    isLegacyLinkMode ? type === expectedLegacyType : type === expectedType;

/**
 * расшифровывает параметры из encoded => base64 => gzip => text => ParsePointsUrlParametersResult
 */
const decryptParameters = params => {
    const decodedParams = decodeURIComponent(params);
    let parsedPoints = PointUrlParameterParser.parse(decodedParams);

    if (parsedPoints) {
        return parsedPoints;
    }

    try {
        const ungzipedParams = pako.ungzip(atob(decodedParams), {to: "string"});
        return PointUrlParameterParser.parse(ungzipedParams);
    } catch (e) {
        return undefined;
    }
};

const checkCoordinates = (geo1, geo2) => {
    return geo1 && geo2 && geo1.lat && geo1.lon && geo2.lat && geo2.lon
        ? geo1.lat === geo2.lat && geo1.lon === geo2.lon
        : false;
};

const isEqualKeyPoints = (p1, p2) => {
    if (!p1 || !p2) {
        return false;
    }
    const result =
        p1.cityId === p2.cityId &&
        p1.isEndPoint === p2.isEndPoint &&
        p1.isIntermediatePoint === p2.isIntermediatePoint &&
        p1.isLoadingPoint === p2.isLoadingPoint &&
        p1.isStartPoint === p2.isStartPoint &&
        p1.isUnloadingPoint === p2.isUnloadingPoint;
    // далее, если результат === true, сравниваем Geo-составляющую:
    // если координаты не null и равны координатам другой точки, считаем точки одинаковыми и на адрес не смотрим.
    if (!result) {
        return false;
    }
    return checkCoordinates(p1.geoPoint, p2.geoPoint) && p1.address === p2.address;
};

export const parseRouteDirectionQueryString = param => {
    try {
        let parsedPoints = decryptParameters(param);
        if (!parsedPoints) {
            parsedPoints = param
                .split(/[;,\-]/)
                .filter(Boolean)
                .map(pair => {
                    const values = pair.split("_");

                    if (values.length !== 2) {
                        return null;
                    }

                    const cityId = parseInt(values[1], 10);
                    const type = parseInt(values[0], 10);

                    if (Number.isNaN(cityId)) {
                        throw new Error(`${values[1]} is not a number`);
                    }

                    return {
                        cityId: cityId,
                        type
                    };
                });
        }

        return parsedPoints.filter(Boolean);
    } catch (e) {
        logger.captureException(e);
        return [];
    }
};

// Создает ключевые точки маршрута на основе точек, переданных в query string.
export const createKeyPointFromParsedPoints = parsedPoints => {
    let previousPoint = null;

    const isLegacyLinkMode = parsedPoints.some(
        pair =>
            pair.type === ROUTE_POINT_TYPES.LEGACY_PREBEGIN_POINT || pair.type === ROUTE_POINT_TYPES.LEGACY_POSTEND_POINT,
    );
    return parsedPoints
        .map((item, index, array) => {
            const point = {
                address: item.address,
                geoPoint: item.geoPoint,
                cityId: item.cityId,
                isCenterCityPoint: !item.geoPoint,
                isStartPoint:
                    index === 0 &&
                    (item.type === ROUTE_POINT_TYPES.START_POINT ||
                        item.type === ROUTE_POINT_TYPES.LOADING_POINT ||
                        item.type === ROUTE_POINT_TYPES.LEGACY_PREBEGIN_POINT),
                isEndPoint:
                    index === array.length - 1 &&
                    (item.type === ROUTE_POINT_TYPES.END_POINT ||
                        item.type === ROUTE_POINT_TYPES.UNLOADING_POINT ||
                        item.type === ROUTE_POINT_TYPES.LEGACY_POSTEND_POINT),
                isIntermediatePoint: item.type === ROUTE_POINT_TYPES.INTERMEDIATE_POINT,
                isLoadingPoint: hasType(
                    item.type,
                    isLegacyLinkMode,
                    ROUTE_POINT_TYPES.LOADING_POINT,
                    ROUTE_POINT_TYPES.START_POINT,
                ),
                isUnloadingPoint: hasType(
                    item.type,
                    isLegacyLinkMode,
                    ROUTE_POINT_TYPES.UNLOADING_POINT,
                    ROUTE_POINT_TYPES.END_POINT,
                ),
            };
            const pointsAreEqual = isEqualKeyPoints(previousPoint, point);
            if (pointsAreEqual) {
                // СПБ (загрузка) - ЕКБ (разгрузка и загрузка) - МСК (разгрузка)
                // e.g.: ?cities=7_1;8_60;7_60;8_3611;
                previousPoint.isLoadingPoint = point.isLoadingPoint;
                return null;
            }

            previousPoint = point;

            const skipPoint =
                !point.isStartPoint &&
                !point.isEndPoint &&
                !point.isIntermediatePoint &&
                !point.isLoadingPoint &&
                !point.isUnloadingPoint;

            return skipPoint ? null : point;
        })
        .filter(Boolean);
};

const ROUTE_POINT_QS_FORMATS = {
    ONLY_CITY_IDS: 1,
    COORDINATES: 2
};

/**
 * Определяет подходящий формат для сохранения маршрута в querystring.
 */
const detectAppropriateFormat = routeRequest => {
    if (!routeRequest.from.isCenterCityPoint || !routeRequest.to.isCenterCityPoint || routeRequest.through.some(point => !point.isCenterCityPoint)) {
        return ROUTE_POINT_QS_FORMATS.COORDINATES;
    }
    return ROUTE_POINT_QS_FORMATS.ONLY_CITY_IDS;
};

/** @param {RouteRequestPoint} routePoint */
const getPointAsOnlyCitiesQueryStringValue = routePoint => {
    if (!routePoint.city && !routePoint.geoPoint) {
        return undefined;
    }

  const cityId = routePoint.getCityId();
  if (routePoint.isUnloadingPoint) {
        return `${ROUTE_POINT_TYPES.UNLOADING_POINT}_${cityId}`;
    }
    if (routePoint.isLoadingPoint) {
        return `${ROUTE_POINT_TYPES.LOADING_POINT}_${cityId}`;
    }
    if (routePoint.isStartPoint) {
        return `${ROUTE_POINT_TYPES.START_POINT}_${cityId}`;
    }
    if (routePoint.isEndPoint) {
        return `${ROUTE_POINT_TYPES.END_POINT}_${cityId}`;
    }
    if (routePoint.isIntermediatePoint) {
        return `${ROUTE_POINT_TYPES.INTERMEDIATE_POINT}_${cityId}`;
    }
    return undefined;
};

/** @param {RouteRequestPoint} routeRequestPoint */
const getPointAsCoordinatesQueryStringValue = routeRequestPoint => {
    if (!routeRequestPoint.city && !routeRequestPoint.geoPoint) {
        return undefined;
    }

    let type;

    if (routeRequestPoint.isUnloadingPoint) {
        type = ROUTE_POINT_TYPES.UNLOADING_POINT;
    } else if (routeRequestPoint.isLoadingPoint) {
        type = ROUTE_POINT_TYPES.LOADING_POINT;
    } else if (routeRequestPoint.isStartPoint) {
        type = ROUTE_POINT_TYPES.START_POINT;
    } else if (routeRequestPoint.isEndPoint) {
        type = ROUTE_POINT_TYPES.END_POINT;
    } else if (routeRequestPoint.isIntermediatePoint) {
        type = ROUTE_POINT_TYPES.INTERMEDIATE_POINT;
    }
    if (!type) {
        return undefined;
    }
    const parsedPoint = new ParsedPoint({
        type: type,
        cityId: routeRequestPoint.isCenterCityPoint ? routeRequestPoint.getCityId() : undefined,
        lat: !routeRequestPoint.isCenterCityPoint ? routeRequestPoint.geoPoint.lat : undefined,
        lon: !routeRequestPoint.isCenterCityPoint ? routeRequestPoint.geoPoint.lon : undefined,
        //address: routePoint.address // TODO: когда будем поддерживать адрес - раскомментировать.
    });
    return parsedPoint.toString();
};

/** @param {RouteRequest} routeRequest */
export const mapRouteDirectionToQueryString = routeRequest => {
    const qsValueFormat = detectAppropriateFormat(routeRequest);
    const items = [];
    const onlyCitiesFormat = qsValueFormat === ROUTE_POINT_QS_FORMATS.ONLY_CITY_IDS;
    const fromPointValue = onlyCitiesFormat ? getPointAsOnlyCitiesQueryStringValue(routeRequest.from) : getPointAsCoordinatesQueryStringValue(routeRequest.from);

    if (fromPointValue) {
        items.push(fromPointValue);
    }

    const throughPointsValues = routeRequest.through
        .reduce((result, current) => {
            result.push(onlyCitiesFormat ? getPointAsOnlyCitiesQueryStringValue(current) : getPointAsCoordinatesQueryStringValue(current));
            return result;
        }, []);

    items.push(...throughPointsValues);

    const toPointValue = onlyCitiesFormat ? getPointAsOnlyCitiesQueryStringValue(routeRequest.to) : getPointAsCoordinatesQueryStringValue(routeRequest.to);
    if (toPointValue) {
        items.push(toPointValue);
    }

    if (onlyCitiesFormat) {
        return items.length > 0 ? items.join("-") : undefined;
    }
    return PointUrlParameterParser.toString(items);
};

export const generateRandomId = () =>
    `${Math.random()
        .toString(36)
        .substring(2, 7)}`;

/**
 * Округляет число с плавающей запятой до указанных знаков после запятой.
 */
export const roundFloat = (number, decimalPlaces) => {
    return Math.round(number * Math.pow(10, decimalPlaces)) / Math.pow(10, decimalPlaces);
};

export const toThousandSeparatedNumber = (number, separator = THIN_SPACE_CHAR) =>
    number && number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, separator);

export const joinWithThinSpace = (...args) => args.join(THIN_SPACE_CHAR);

export const toTitleCase = text => text !== null && text.substring(0, 1).toUpperCase() + text.substring(1, text.length);

/** @param {RouteResponsePoint} routePoint */
export const createMapKey = routePoint => `${routePoint.geoPoint.lat}_${routePoint.geoPoint.lon}`;

export const headerBottomHeight = () => {
    const bottomMenu = document.getElementById("header-bottom-menu");
    return bottomMenu ? bottomMenu.offsetHeight : 0;
};
